import { create } from 'zustand';
import apiClient from '../api/client';

export type TaskSection = 'daily' | 'project';
export type TaskStatus = 'todo' | 'in_progress' | 'done';

export interface Task {
  id: string;
  title: string;
  description: string;
  section: TaskSection;
  status: TaskStatus;
  due_date: string | null;
  note_id: string;
  note_title: string;
  calendar_event_id: string | null;
  calendar_html_link: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface TaskCreatePayload {
  title: string;
  description: string;
  section: TaskSection;
  due_date?: string | null;
  note_id?: string;
}

interface TasksState {
  tasks: Task[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  createTask: (payload: TaskCreatePayload) => Promise<Task | undefined>;
  updateTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  deleteTask: (taskId: string) => Promise<boolean>;
  clearError: () => void;
}

export const useTasksStore = create<TasksState>((set) => ({
  tasks: [],
  loading: false,
  saving: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await apiClient.get<{ tasks: Task[] }>('/api/tasks');
      set({ tasks: data.tasks, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  createTask: async (payload) => {
    set({ saving: true, error: null });
    try {
      const created = await apiClient.post<Task>('/api/tasks', payload);
      set((state) => ({
        tasks: [created, ...state.tasks],
        saving: false,
      }));
      return created;
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
      return undefined;
    }
  },

  updateTaskStatus: async (taskId, status) => {
    set({ error: null });
    try {
      const updated = await apiClient.put<Task>(`/api/tasks/${taskId}/status`, { status });
      set((state) => ({
        tasks: state.tasks.map((task) => (task.id === taskId ? updated : task)),
      }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  deleteTask: async (taskId) => {
    set({ error: null });
    try {
      await apiClient.delete<{ status: string; id: string }>(`/api/tasks/${taskId}`);
      set((state) => ({
        tasks: state.tasks.filter((task) => task.id !== taskId),
      }));
      return true;
    } catch (err) {
      set({ error: (err as Error).message });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
